import { runInAction } from 'mobx';
import React from 'react';
import { mg } from '../mg';

type IExternalGet = (entity: any, fieldName: string, dotIndex?: number) => any;

export interface IMgColumnAccessors<T=any> {
	readonly name: string
	getter() : T
	setter(value: T) : void
	handleChange(event: {target: {value: T, type?: string}}) : void
}

export interface IMgColumn<T=any, E=Record<string, any>> {
	readonly descriptor: string
	readonly name: string
	readonly fieldName: string
	readonly label?: string
	readonly type: string
	readonly isRequired: boolean
	readonly isReadonly: boolean
	readonly emptyValue: T
	readonly flexBase: number
	readonly flexGrow: number

	externalGet: IExternalGet|null

	sanitize(value: any, entity: E) : T
	comparator(entity1: E, entity2: E, fieldName: string) : number
	stringValue(entity: E) : string
	matches(entity: E, filterValue: string) : boolean
	bodyCellRenderer(entity: E, style?: React.CSSProperties | undefined) : React.ReactNode

	setterMaker(value: T) : (entity: E) => void
	accessors(entity: E) : IMgColumnAccessors<T>
}

export class MgColumn<T=any, E=Record<string, any>> implements IMgColumn<T, E> {
	private constructor(base: Partial<IMgColumn<T, E>>, override: Partial<IMgColumn<T, E>>) {
		const keys = Object.keys(this) as Array<keyof IMgColumn<T, E>>;
		for (const k of keys) {
			(this as any)[k] = override?.[k] ?? base?.[k] ?? this[k];
		}
		this.setterMaker = (value: any) => (entity: any) => runInAction(() => (entity[this.fieldName] = this.sanitize(value, entity)));
		this.accessors = (entity: E) => {
			const self = this;
			const dotIndex = self.fieldName.indexOf('.');
			return {
				name: self.name,
				getter() {
					if (dotIndex===-1) return entity[self.fieldName];
					if (!mg.assertPresent(self.externalGet)) return undefined;
					return self.externalGet(entity, self.fieldName, dotIndex);
				},
				setter(value: T) {
					console.log(JSON.stringify(value));
					self.setterMaker(value)(entity);
				},
				handleChange(event: {target: {value: T, type?: string}}) : void {
					const t = event.target;
					const value = t.type==='checkbox' ? !entity[self.fieldName] : self.sanitize(t.value, entity);
					self.setterMaker(value as T)(entity);
				}
			};
		};

		this.sanitize = this.sanitize.bind(this);
		this.comparator = this.comparator.bind(this);
		this.stringValue = this.stringValue.bind(this);
		this.matches = this.matches.bind(this);
		this.bodyCellRenderer = this.bodyCellRenderer.bind(this);

		this.setterMaker = this.setterMaker.bind(self);
		this.accessors = this.accessors.bind(self);
	}

	static _BASE = new MgColumn({}, {});

	with(override: Partial<IMgColumn<T, E>>) : MgColumn<T, E> {
		return new MgColumn(this, override);
	}

	readonly name = '';
	readonly fieldName = '';
	readonly label: string|undefined;
	readonly type = '';
	readonly isRequired = false;
	readonly isReadonly = false;
	readonly emptyValue = undefined as unknown as T;
	readonly flexBase = 0;
	readonly flexGrow = 1;

	externalGet: IExternalGet|null = null;

	get descriptor() {
		const prefix = this.isReadonly ? '/' : this.isRequired ? '*' : '';
		const sep = this.name===this.fieldName ? ' ' : '';
		const label = this.label ? ' =' + this.label : '';
		return prefix + this.name + sep + ':' + this.type + label;
	}
	readonly sanitize = (value: any, entity: E) : T => value as unknown as T;
	readonly setterMaker: (value: any) => (entity: any) => void;
	readonly accessors: (entity: E) => IMgColumnAccessors<T>;
	readonly comparator = (entity1: E, entity2: E) : number => {
		const a = entity1[this.fieldName];
		const b = entity2[this.fieldName];
		return a<b ? -1 : a>b ? 1 : 0;
	};

	readonly stringValue = (entity: E) : string => {
		const a = entity[this.fieldName];
		if (mg.isNullOrUndefined(a)) return '';
		return '' + a;
	}

	readonly matches = (entity: E, filterValue: string) : boolean => !!~this.stringValue(entity).indexOf(filterValue);

	readonly bodyCellRenderer = (entity: E, style?: React.CSSProperties | undefined) : React.ReactNode => React.createElement('div', {style}, this.stringValue(entity));
}

export interface MgColumnExtras {
	members?: Array<{ id: string | number, text: string }>
	errorText?: string | boolean
	hidden?: boolean
	min?: string|number
	max?: string|number
}

export class MgColumnist {
	constructor(public readonly blocks: ReadonlyArray<ReadonlyArray<IMgColumn>>) {}

	readonly newEntity = <E extends object>(original?: E) : E => {
		const r = {} as E;
		this.blocks.forEach(b => b.forEach(c => r[c.fieldName] = c.emptyValue));
		return {...r, ...original};
	}

	get length() {
		return this.blocks.length;
	}

	get columns() {
		return this.blocks.flat();
	}

	get descriptors() {
		return this.columns.map(c => c.descriptor);
	}

	newExtras = (entity: object, customization: Record<string, MgColumnExtras>) : Record<string, MgColumnExtras> => {
		const r: Record<string, MgColumnExtras> = {};
		this.columns.forEach(c => r[c.name] = {...customization[c.name]});
		this.columns.forEach(c => {
			const o: MgColumnExtras = r[c.name];
			if (c.isRequired && !o.hidden && mg.shallowEqual(c.accessors(entity).getter(), c.emptyValue)) o.errorText = true;
		});
		return r;
	}

	getBlockErrors(index: number, extras: Record<string, MgColumnExtras>) : Array<{label: string, error: true|string}> {
		return this.blocks[index]
			.map(c => ({label: c.label || c.name, error: extras[c.name]?.errorText}))
			.filter(({error}) => error) as any;
	}

	setExternalGet(externalGet: IExternalGet) {
		this.columns.forEach(c => c.externalGet = externalGet);
	}
}

export class MgColumnFactory {
	private typeMap = new Map<string, MgColumn>();

	register(override: Partial<IMgColumn> & {baseType?: string}) {
		const baseType = override.baseType;
		const base = !baseType ? MgColumn._BASE : this.typeMap.get(baseType);
		if (!mg.checkPresent(base, 'Unknown type in', override)) throw new Error();
		const c = base.with(override);
		this.typeMap.set(c.type, c);
	}

	create<T=any, E=Record<string, any>>(descriptor: string, override?: Partial<IMgColumn<T, E>>) : MgColumn<T, E> {
		const m = descriptor.match(/^([*/]?)(\w+(?:\.\w+)*)(?:( ?):(\w+))?(?: =(\w+))?$/);
		if (!mg.checkTrue(m, 'Illegal descriptor', descriptor) || !m) throw new Error();
		const isRequired = m[1] === '*';
		const isReadonly = m[1] === '/';
		const fieldName = m[2];
		const composeName = m[3] === '';
		const type = m[4] ?? 'string';
		const label = m[5];
		const name = composeName ? fieldName + ':' + type : fieldName;
		const isEnum = !!type.match(/^[A-Z]/);
		const base = this.typeMap.get(isEnum ? 'enum' : type);
		if (!mg.checkPresent(base, 'Unknown type in', descriptor)) throw new Error();
		const result = base.with({ name, fieldName, label, type, isRequired, isReadonly, ...override } as any);
		if (!mg.checkTrue(result.accessors({}).name, 'Broken column', descriptor)) throw new Error();//TODO
		return result;
	}

	createColumnist(descriptors: Array<string|null>) : MgColumnist {
		const r = new Array<Array<IMgColumn>>();
		let a = new Array<IMgColumn>();
		for (const c of descriptors) {
			if (c === null) {
				r.push(a);
				a = new Array<IMgColumn>();
			} else {
				a.push(this.create(c));
			}
		}
		r.push(a);
		return new MgColumnist(r);
	}
}

export const mgColumnFactory = new MgColumnFactory();

function sanitizeNumber(value: any) : number {
	console.log(value, typeof value);
	const x = +value;
	return isFinite(x) ? x : 0;
}

function sanitizeInt(value: any) : number {
	return Math.round(sanitizeNumber(value));
}

const standardTypes = [
	{
		type: 'string',
		emptyValue: '',
		sanitize(this: IMgColumn, value: any, entity: any) {
			return '' + (value ?? '');
		},
		comparator(this: IMgColumn, entity1: any, entity2: any) {
			const a = entity1[this.fieldName];
			const b = entity2[this.fieldName];
			return mg.caseHalfSensitiveCompare(a, b);
		}
	},
	{
		baseType: 'string',
		type: 'enum',
	},
	{
		type: 'number',
		emptyValue: 0,
		sanitize(this: IMgColumn, value: any, entity: any) : number {
			return sanitizeNumber(value);
		},
	},
	{
		baseType: 'number',
		type: 'int',
		sanitize(this: IMgColumn, value: any, entity: any) : number {
			return sanitizeInt(value);
		},
	},
	{
		baseType: 'int',
		type: 'posInt',
		sanitize(this: IMgColumn, value: any, entity: any) : number {
			return Math.max(1, sanitizeInt(value));
		},
	},
	{
		baseType: 'int',
		type: 'nonNegInt',
		sanitize(this: IMgColumn, value: any, entity: any) : number {
			return Math.max(0, sanitizeInt(value));
		},
	},
	{
		type: 'bool',
		emptyValue: false,
		sanitize(value: any, entity: any) : boolean {
			return !!value;
		},
	},
	{
		type: 'datetime',
		emptyValue: '',
		sanitize(value: any, entity: any) : string {
			return typeof value !== 'string' ? '' : value;
		},
	},
	{
		baseType: 'string',
		type: 'idsAsString',
		emptyValue: '',
		sanitize(value: any, entity: any) : string {
			return typeof value !== 'string' ? '' : value; //TODO
		},
	},
	{
		baseType: 'string',
		type: 'color',
		emptyValue: '',
		sanitize(value: any, entity: any) : string {
			return typeof value !== 'string' ? '' : value; //TODO
		},
	},
	{
		baseType: 'string',
		type: 'imageLink',
		emptyValue: '',
		sanitize(value: any, entity: any) : string {
			return typeof value !== 'string' ? '' : value; //TODO
		},
	},
	{
		type: 'variationsBase', //TODO
		emptyValue: [{id: 0, name: ''}],
		sanitize(value: any, entity: any) {
			return !Array.isArray(value) ? [{id: 0, name: ''}] : value;
		},
	},
	{
		type: 'variationsMore', //TODO
		emptyValue: [],
		sanitize(value: any, entity: any) {
			return !Array.isArray(value) ? [] : value;
		},
	},
];

standardTypes.forEach(t => mgColumnFactory.register(t));
